import * as React from 'react';
import { createPortal } from 'react-dom';
import { useRef, useEffect, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import { useCommandBar } from './useCommandBar';
import { useAiChat } from '../AiChat/useAiChat';
import { useChatSessions } from './useChatSessions';
import AiChatMessages from '../AiChat/AiChatMessages';
import AiChatInput from '../AiChat/AiChatInput';
import AiAvatar from '../AiChat/AiAvatar';
import CommandBarResults from './CommandBarResults';
import './command-bar.css';
import '../AiChat/ai-chat.css';

type Props = {
  onClose: () => void;
};

const MESSAGE_LIMIT = 20;

// One door. The Assistant button and ⌘K both open the same bar, which now leads
// with the assistant — so the button keeps its promise without needing a
// separate chat-first entrance.
const CommandBar: React.FC<React.PropsWithChildren<Props>> = ({ onClose }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const [mode, setMode] = useState<'search' | 'chat'>('search');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const lastSavedKeyRef = useRef<string>('');

  const {
    messages,
    isLoading: aiIsLoading,
    pendingMessage,
    pendingConfirm,
    error: aiError,
    activeToolName,
    sendMessage,
    confirmTool,
    cancelTool,
    reset: resetChat,
    retry: retryChat,
    restoreMessages,
    messageCount,
    submitFeedback,
  } = useAiChat();

  const { sessions, saveSession, removeSession, clearAllSessions, getSession } =
    useChatSessions();

  const saveSessionDebounced = useCallback(
    (sessionId: string | null, msgs: typeof messages): string => {
      const key = `${sessionId ?? 'new'}:${msgs.length}`;
      if (lastSavedKeyRef.current === key) return sessionId ?? '';
      const id = saveSession(sessionId, msgs);
      lastSavedKeyRef.current = `${id}:${msgs.length}`;
      return id;
    },
    [saveSession],
  );

  const handleAiSelect = useCallback(
    (q: string) => {
      resetChat();
      setActiveSessionId(null);
      setMode('chat');
      sendMessage(q);
    },
    [sendMessage, resetChat],
  );

  const {
    query,
    setQuery,
    sections,
    selectedIndex,
    activeItemId,
    handleSelect,
    handleKeyDown,
  } = useCommandBar(onClose, handleAiSelect);

  const handleClose = useCallback(() => {
    if (mode === 'chat' && messages.length > 0) {
      const id = saveSessionDebounced(activeSessionId, messages);
      setActiveSessionId(id);
    }
    setConfirmingClear(false);
    onClose();
  }, [mode, messages, saveSessionDebounced, activeSessionId, onClose]);

  const handleNewChat = useCallback(() => {
    if (mode === 'chat' && messages.length > 0) {
      saveSessionDebounced(activeSessionId, messages);
    }
    resetChat();
    setActiveSessionId(null);
    lastSavedKeyRef.current = '';
    setQuery('');
    setMode('search');
    inputRef.current?.focus();
  }, [
    mode,
    messages,
    saveSessionDebounced,
    activeSessionId,
    resetChat,
    setQuery,
  ]);

  const handleResumeSession = useCallback(
    (sessionId: string) => {
      const session = getSession(sessionId);
      if (!session) return;
      setActiveSessionId(sessionId);
      restoreMessages(session.messages);
      setMode('chat');
    },
    [getSession, restoreMessages],
  );

  const handleRemoveSession = useCallback(
    (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      removeSession(sessionId);
    },
    [removeSession],
  );

  const handleNavigate = useCallback(
    (route: string) => {
      if (mode === 'chat' && messages.length > 0) {
        const id = saveSessionDebounced(activeSessionId, messages);
        setActiveSessionId(id);
      }
      history.push(route);
      onClose();
    },
    [history, onClose, mode, messages, saveSessionDebounced, activeSessionId],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (mode !== 'chat') return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mode, handleClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  return createPortal(
    <div
      className={`command-bar-backdrop${
        mode === 'chat' ? ' command-bar-backdrop-chat' : ''
      }`}
      onClick={handleBackdropClick}
      data-testid="command-bar">
      <div
        className={`command-bar-container${
          mode === 'chat' ? ' command-bar-chat-mode ai-chat-root' : ''
        }`}>
        {mode === 'search' ? (
          <>
            {/* The star, not a magnifier: the bar's first job is to be asked a
                question. */}
            <div className="command-bar-input-row command-bar-input-row--ai">
              <span className="command-bar-input-icon">
                <FlairIcon icon="phosphor-star-four" size="lg" />
              </span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('aiChat.commandBar.searchPlaceholder')}
                className="command-bar-input"
                role="combobox"
                aria-expanded={sections.length > 0}
                aria-controls="command-bar-results"
                aria-activedescendant={activeItemId}
              />
              <span className="command-bar-shortcut">
                <kbd>⌘K</kbd>
              </span>
            </div>
            <CommandBarResults
              id="command-bar-results"
              sections={sections}
              selectedIndex={selectedIndex}
              onSelect={handleSelect}
            />
            {sessions.length > 0 && query.trim() === '' && (
              <div className="command-bar-sessions">
                <div className="command-bar-sessions-header">
                  <span className="command-bar-section-title">
                    Recent Chats
                  </span>
                  {confirmingClear ? (
                    <div className="command-bar-clear-confirm">
                      <button
                        type="button"
                        className="command-bar-clear-all command-bar-clear-all--danger"
                        onClick={() => {
                          clearAllSessions();
                          setConfirmingClear(false);
                        }}>
                        Confirm clear all
                      </button>
                      <button
                        type="button"
                        className="command-bar-clear-all"
                        onClick={() => setConfirmingClear(false)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="command-bar-clear-all"
                      onClick={() => setConfirmingClear(true)}>
                      Clear all
                    </button>
                  )}
                </div>
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className="command-bar-session-item"
                    onClick={() => handleResumeSession(session.id)}>
                    <AiAvatar size="icon" />
                    <span className="command-bar-session-title">
                      {session.title}
                    </span>
                    <button
                      className="command-bar-session-remove"
                      onClick={(e) => handleRemoveSession(e, session.id)}>
                      <FlairIcon icon="close-outline" size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="command-bar-footer">
              <span className="command-bar-footer-hint">
                <kbd>↑↓</kbd> Navigate
              </span>
              <span className="command-bar-footer-hint">
                <kbd>↵</kbd> Select
              </span>
              <span className="command-bar-footer-hint">
                <kbd>ESC</kbd> Close
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="command-bar-chat-header">
              <div className="command-bar-chat-header-left">
                <span className="command-bar-chat-title">
                  Employee Intelligence
                </span>
              </div>
              <div className="command-bar-chat-header-right">
                <button
                  type="button"
                  className="ai-chat-modal-close-btn"
                  onClick={handleNewChat}
                  title="New chat"
                  aria-label="New chat">
                  <FlairIcon icon="add-outline" size="sm" />
                </button>
                <button
                  type="button"
                  className="ai-chat-modal-close-btn"
                  onClick={handleClose}
                  title="Close"
                  aria-label="Close">
                  <FlairIcon icon="close-outline" size="sm" />
                </button>
              </div>
            </div>
            <div className="command-bar-chat-body">
              <AiChatMessages
                messages={messages}
                isLoading={aiIsLoading}
                activeToolName={activeToolName}
                pendingConfirm={pendingConfirm}
                onConfirm={confirmTool}
                onCancel={cancelTool}
                onSuggestionClick={sendMessage}
                onNavigate={handleNavigate}
                onRetry={retryChat}
                onFeedback={submitFeedback}
              />
            </div>
            <div className="command-bar-chat-footer">
              {aiError && <div className="ai-chat-modal-error">{aiError}</div>}
              <AiChatInput
                onSend={sendMessage}
                isLimitReached={messageCount >= MESSAGE_LIMIT}
                pendingMessage={pendingMessage}
                onStartNewChat={handleNewChat}
              />
              <p className="command-bar-chat-disclaimer">
                {t('aiChat.commandBar.disclaimer')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CommandBar;
